// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { matchPath, useLocation } from "react-router-dom";
import { useTypedParams } from "react-router-typesafe-routes/dom";

import { SimpleLinkProps } from "~@jii/common-ui";
import { useSingleResidentContext } from "~@jii/data";
import { State } from "~@jii/paths";
import { useUsAzTranslations } from "~@jii/translation";

import { useShowIONBanner } from "../components/IONReferral/useShowIONBanner";
import { useUsAzSingleResidentContext } from "../components/UsAzSingleResidentContext/UsAzSingleResidentContext";

export function useInfoPageFooterLinks(): Array<SimpleLinkProps> {
  const { isDprQualified } = useUsAzSingleResidentContext();
  const { t } = useUsAzTranslations();
  const { pathname } = useLocation();
  const pathParams = useTypedParams(State.Resident.UsAzMoreInformation);
  const showION = useShowIONBanner();
  const {
    residentFlags: { usAzClassification },
  } = useSingleResidentContext();

  const links: Array<SimpleLinkProps> = [];
  if (!matchPath(State.Resident.path, pathname)) {
    links.push({
      children: t(($) => $.homePageLinkText),
      to: State.Resident.buildPath(pathParams),
    });
  }

  if (!matchPath(State.Resident.UsAzMoreInformation.About.path, pathname)) {
    links.push({
      to: State.Resident.UsAzMoreInformation.About.buildPath(pathParams),
      children: t(($) => $.about.heading),
    });
  }
  if (
    showION &&
    !matchPath(State.Resident.UsAzMoreInformation.ION.path, pathname)
  ) {
    links.push({
      to: State.Resident.UsAzMoreInformation.ION.buildPath(pathParams),
      children: t(($) => $.ion.infoPage.heading),
    });
  }
  if (
    !matchPath(State.Resident.UsAzMoreInformation.ImportantDates.path, pathname)
  ) {
    links.push({
      to: State.Resident.UsAzMoreInformation.ImportantDates.buildPath(
        pathParams,
      ),
      children: t(($) => $.importantDates.moreInfo.heading),
    });
  }
  if (usAzClassification) {
    if (!matchPath(State.Resident.UsAzClassificationPoints.path, pathname)) {
      links.push({
        to: State.Resident.UsAzClassificationPoints.buildPath(pathParams),
        children: t(($) => $.classificationPoints.page.heading),
      });
    }
    if (
      !matchPath(State.Resident.UsAzClassificationPoints.FAQ.path, pathname)
    ) {
      links.push({
        to: State.Resident.UsAzClassificationPoints.FAQ.buildPath(pathParams),
        children: t(($) => $.classificationPoints.faq.heading),
      });
    }
  }
  if (
    isDprQualified &&
    !matchPath(State.Resident.UsAzMoreInformation.DPR.path, pathname)
  ) {
    links.push({
      to: State.Resident.UsAzMoreInformation.DPR.buildPath(pathParams),
      children: t(($) => $.dprInfoPage.heading),
    });
  }
  return links;
}
